import { defineComponent as _defineComponent } from 'vue'
import {useNotify} from '@/hooks/useNotify';
import {useTranslations} from '@/hooks/useTranslations';
import {ref} from 'vue';
import {ClaimType} from '@/models/Claims/ClaimType';
import ClaimsRepository from '@/utils/API/ClaimsRepository';
import {NutrientClaims} from '@/models/Claims/NutrientClaims';
import {ITab} from './proptypes/ITab';
import {RecipeUserClaim} from '@/models/Claims/RecipeUserClaim';
import {Claim} from '@/models/Claims/Claim';
import {ClaimTextOptions} from "@/models/Claims/ClaimTextOptions";
import {NutritionLabelCountry} from "@/models/NutritionLabels/NutritionLabelCountry";

import VSelect from "@/components/vSelect.vue";
import VModal from "@/components/vModal.vue";
import VTabbedContent from "@/components/vTabbedContent.vue";
import VPreLoader from "@/components/vPreLoader.vue";
import VAccordion from "@/components/vAccordion.vue";
import VGenericConfirmationModal from "@/components/vGenericConfirmationModal.vue";

type vClaimsModalProps = {
  restaurantId: number;
  showClaimModal: boolean;
  recipeId: number;
  countryId: NutritionLabelCountry,
  referenceAmount: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'vClaimsAndStatementsModal',
  props: {
    restaurantId: null,
    showClaimModal: { type: Boolean, default: false },
    recipeId: null,
    countryId: null,
    referenceAmount: null
  },
  emits: ["on-cancel", "on-save"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'on-cancel'): void;
  (e: 'on-save'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { restaurantId: number, showClaimModal: boolean, recipeId: number, countryId: NutritionLabelCountry, referenceAmount: number };





const notifyService = useNotify();

const translations = useTranslations('common', 'recipes');

const nutrients = ref<NutrientClaims[]>([]);
const recipeUserClaims = ref<RecipeUserClaim[]>([]);
const showDeleteClaimConfirmationModal = ref(false);
const isSystemClaimsLoading = ref(true);
const isUserClaimsLoading = ref(true);
const claimToDeleteIndex = ref<number>();

function selectedOrDefaultClaimTextOption(options: ClaimTextOptions[]) {
  const selectedOption = options.find((x) => x.isSelected);

  if (selectedOption) {
    return selectedOption;
  }

  options[0].isSelected = true;
  return options[0];
}

function initializeClaims() {
  fetchNutrientClaims();
  fetchRecipeUserClaims();
}

function getClaimNameText(claim: Claim, nutrient: NutrientClaims) {
  // Lean/extra lean should only display as is
  if (claim.claimType == ClaimType.Lean || claim.claimType == ClaimType.ExtraLean) {
    return translateClaimType(claim.claimType);
  }

  return `${translateClaimType(claim.claimType)} ${nutrient.nutrientName}`;
}

function fetchNutrientClaims() {
  ClaimsRepository.getClaimsForRecipe(props.recipeId, props.countryId).then((nutrientClaims) => {
    if (nutrientClaims && nutrientClaims.data) {
      nutrients.value = nutrientClaims.data;
      isSystemClaimsLoading.value = false;
    }
  }).catch((error) => {
    notifyService.error("Error fetching nutrient claims:", error);
  });
}

function fetchRecipeUserClaims() {
  ClaimsRepository.getRecipeUserClaims(props.recipeId, props.countryId).then((response) => {
    if (response && response.data) {
      mapUserClaims(response.data);
    }
  }).catch((error) => {
    notifyService.error("Error fetching user defined claims:", error);
  });
}

function mapUserClaims(userClaims: RecipeUserClaim[]) {
  recipeUserClaims.value = userClaims;
  isUserClaimsLoading.value = false;
}

function handleCheckboxSelection(nutrient: NutrientClaims) {
  if (!nutrient.claims.some((claim) => claim.isEnabled)) {
    nutrient.claims[0].isEnabled = true;
  }
  else {
    nutrient.claims.forEach((claim) => {
      claim.isEnabled = false;
    });
  }
}

function handleRadioBtnSelection(claim: Claim, nutrient: NutrientClaims) {
  claim.isEnabled = true;
  nutrient.isSelected = true;
  nutrient.claims
    .filter((filterable) => filterable.id !== claim.id)
    .forEach((claimObj) => {
      claimObj.isEnabled = false;
    });
}

function translateClaimType(claimType: ClaimType) {
  const claimTypeMap: { [key in ClaimType]: string } = {
    [ClaimType.NoneApplicable]: 'Not Applicable',
    [ClaimType.NoneAdded]: 'No Added',
    [ClaimType.Free]: 'Free',
    [ClaimType.VeryLow]: 'Very Low',
    [ClaimType.Low]: 'Low',
    [ClaimType.Lean]: 'Lean',
    [ClaimType.ExtraLean]: 'Extra Lean',
    [ClaimType.GoodSource]: 'Good Source of',
    [ClaimType.SourceContains]: 'Source contains',
    [ClaimType.High]: 'High',
    [ClaimType.ExcellentSource]: 'Excellent Source'
  };

  return claimTypeMap[claimType] || 'Unknown claim';
}

function saveChanges() {
  const updatedNutrientClaims : NutrientClaims[] = nutrients.value.map((nutrient) => {
    return {
      nutrientName: nutrient.nutrientName,
      nutrientType: nutrient.nutrientType,
      isSelected: nutrient.isSelected,
      claims: nutrient.claims.map((claim) => {
        return {
          ...claim,
          claimTextOptions: [claim.claimTextOptions.find((option) => option.isSelected)],
        };
      }),
      warningText: nutrient.warningText,
    };
  });

  ClaimsRepository.saveClaims(props.recipeId, updatedNutrientClaims, recipeUserClaims.value)
    .then((response) => {
      notifyService.success("Claims saved successfully.");
      mapUserClaims(response.data.userClaims);
      emit('on-save');
      closeClaimModal();
    })
    .catch((error) => {
      notifyService.error(error.message);
    });
}

function handleClaimTextSelection(claim: Claim, selectedOption: ClaimTextOptions) {
  claim.claimTextOptions.forEach((option) => {
    option.isSelected = (option.name === selectedOption.name);
  });
}

function updateUserClaimText(recipeUserClaim: RecipeUserClaim) {
  if (recipeUserClaim.userClaim.id){
    ClaimsRepository.updateUserClaimText(props.restaurantId, recipeUserClaim)
      .then(() => {
        notifyService.success('User claim updated successfully.');
      })
      .catch((error) => {
        notifyService.error(error.message);
      });
  }
}

function closeClaimModal() {
  emit("on-cancel");
}

const tabs: ITab[] = [
  { id: 1, name: "Nutrient Claims" },
  { id: 2, name: "Recipe Claims" },
];

function removeClaim(index: number) {
  claimToDeleteIndex.value = index;

  let claimToDelete = recipeUserClaims.value[index].userClaim;

  if(!claimToDelete.id && claimToDelete.name== ""){
    removeUserClaimsFromList();
  }
  else{
    showDeleteClaimConfirmationModal.value = true;
  }
}

function submitUserClaimDeletionConfirmation() {
  let claimToDelete = recipeUserClaims.value[claimToDeleteIndex.value].userClaim;

  if(!claimToDelete.id){
    removeUserClaimsFromList();
    showDeleteClaimConfirmationModal.value = false;
    return;
  }

  if (claimToDeleteIndex.value !== null) {
    ClaimsRepository.deleteUserClaim(props.restaurantId, claimToDelete.id)
      .then(() => {
        removeUserClaimsFromList();

        notifyService.success('User claim deleted successfully.');
        showDeleteClaimConfirmationModal.value = false;
      })
      .catch((error) => {
        notifyService.error(error.message);
        showDeleteClaimConfirmationModal.value = false;
      });
  }
}

function removeUserClaimsFromList(){
  recipeUserClaims.value.splice(claimToDeleteIndex.value, 1);
  claimToDeleteIndex.value = null;
}

function closeUserClaimDeletionModal() {
  showDeleteClaimConfirmationModal.value = false;
  claimToDeleteIndex.value = null;
}

function addClaim() {
  recipeUserClaims.value.push({ userClaim: { name: '', restaurantId: props.restaurantId }, isEnabled: false});
}


return { __sfc: true,props, emit, notifyService, translations, nutrients, recipeUserClaims, showDeleteClaimConfirmationModal, isSystemClaimsLoading, isUserClaimsLoading, claimToDeleteIndex, selectedOrDefaultClaimTextOption, initializeClaims, getClaimNameText, fetchNutrientClaims, fetchRecipeUserClaims, mapUserClaims, handleCheckboxSelection, handleRadioBtnSelection, translateClaimType, saveChanges, handleClaimTextSelection, updateUserClaimText, closeClaimModal, tabs, removeClaim, submitUserClaimDeletionConfirmation, removeUserClaimsFromList, closeUserClaimDeletionModal, addClaim, VSelect, VModal, VTabbedContent, VPreLoader, VAccordion, VGenericConfirmationModal }
}

})